import * as React from 'react';
import { client } from '@studio/api/api-client/client';
import { CENTRAL_API_V3 } from '@studio/constants/env-variables';
import { ErrorConstants } from '@studio/constants/error-constants';
import { extractCentralErrors } from '@studio/helpers/errorHandling';
import type { Authentication } from '@studio/types';
import { fetchMe } from '@studio/storeV2/authentication/helpers/fetch-me';
import { fetchMePermissions } from '@studio/storeV2/authentication/helpers/fetch-me-permissions';
import * as SessionStore from '@studio/storeV2/authentication/session-store';
import { CentralResponseError } from '@studio/types';
import type { ApiClientConfig } from '@studio/types/axios';

const _TOKEN_URL = `${CENTRAL_API_V3}/auth/token`;

/**
 * Full session refresh (was `handleRefreshToken` / `APP_RELOAD`). Triggered only by `AuthProvider`
 * on mount. Cookie-grant token → fetchMe → permissions. Customer loads via `useCustomer({ fetchOnMount: true })`
 * at authenticated-app mount. **No `reset()`** — refresh extends the same session (STUD-5737). Invalid refresh
 * token → `setTokenExpired()`.
 */
const useAppRefresh = () => {
  const refresh = React.useCallback(async () => {
    SessionStore.session.setIsRefreshing(true);
    try {
      const config: ApiClientConfig = { data: { grant_type: 'refresh_token_cookie' }, withCredentials: true };
      const tokenResponse: Authentication.Post.Token = await client(_TOKEN_URL, config);
      SessionStore.session.setAccessToken(tokenResponse.access_token);
      SessionStore.session.setAccessTokenTimestamp(Date.now());
      await fetchMe();
      await fetchMePermissions();
    } catch (err) {
      if (err instanceof CentralResponseError) {
        const errors = extractCentralErrors(err.response.data);
        if (errors.some((error) => error.code === ErrorConstants.CENTRAL_INVALID_REFRESH_TOKEN)) {
          SessionStore.session.setTokenExpired();
        }
      } else if (err instanceof Error) {
        console.error(err);
      }
    } finally {
      SessionStore.session.setIsRefreshing(false);
    }
  }, []);

  return { refresh };
};

export default useAppRefresh;
